import { useEffect, useState } from 'react'
import { Search, Filter, Zap } from 'lucide-react'
import { api } from '../lib/api'
import clsx from 'clsx'

interface Nudge {
  id: string
  category: string
  title: string
  body: string
  channel: string
  system_target: string
  science_source: string | null
  effectiveness_score: number
}

const CATEGORIES = ['all', 'leadership', 'safety', 'burnout', 'inclusion', 'recognition', 'goals']

/** Warm-tinted chips — ink text on soft fills */
const CATEGORY_COLORS: Record<string, string> = {
  leadership:  'bg-[#EBF6FB] text-ink border-[#a7d8e0]',
  safety:      'bg-[#EDE8F5] text-ink border-[#d4cce8]',
  burnout:     'bg-[#fdf2f2] text-ink border-[#e8cfcf]',
  inclusion:   'bg-[#eef5ec] text-ink border-[#B8C7B1]',
  recognition: 'bg-[#fdf8f0] text-ink border-[#e8d4b8]',
  goals:       'bg-[#E8F0EE] text-ink border-brand-sage',
}

export function NudgeLibraryPage() {
  const [nudges, setNudges] = useState<Nudge[]>([])
  const [loading, setLoading] = useState(true)
  const [category, setCategory] = useState('all')
  const [search, setSearch] = useState('')
  const [preview, setPreview] = useState<Nudge | null>(null)

  useEffect(() => {
    setLoading(true)
    const params: Record<string, string> = { limit: '200' }
    if (category !== 'all') params.category = category

    api.getNudges(params)
      .then(r => setNudges((r as { nudges: Nudge[] }).nudges))
      .finally(() => setLoading(false))
  }, [category])

  const filtered = nudges.filter(n =>
    !search || n.title.toLowerCase().includes(search.toLowerCase()) || n.body.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="mb-7">
        <h1 className="text-2xl font-bold text-ink" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>Nudge Library</h1>
        <p className="text-sm mt-1" style={{color: '#6b8a8f'}}>Browse and preview science-backed behavioral prompts</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
          <input
            type="text"
            placeholder="Search nudges…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-warm-card border border-warm-border rounded-xl pl-9 pr-4 py-2.5 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand-teal transition-colors shadow-card"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className="px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors border"
              style={category === cat ? {
                background: '#2A7A8A', color: '#fff', borderColor: '#2A7A8A',
                boxShadow: '0 0 20px -6px rgba(42,122,138,0.4)'
              } : {
                background: '#FDFCF8', color: '#6b8a8f', borderColor: '#e0d9cc'
              }}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <p className="text-xs text-ink-soft mb-4 flex items-center gap-1.5">
        <Filter size={12} />
        {filtered.length} nudge{filtered.length !== 1 ? 's' : ''}
      </p>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-warm-card border border-warm-border rounded-2xl p-5 h-36 animate-pulse shadow-card" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {filtered.map(nudge => (
            <NudgeCard
              key={nudge.id}
              nudge={nudge}
              onClick={() => setPreview(nudge)}
            />
          ))}
          {filtered.length === 0 && (
            <div className="col-span-2 text-center py-16 text-ink-muted text-sm">
              No nudges match your search.
            </div>
          )}
        </div>
      )}

      {preview && (
        <NudgePreviewModal nudge={preview} onClose={() => setPreview(null)} />
      )}
    </div>
  )
}

function NudgeCard({ nudge, onClick }: { nudge: Nudge; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left bg-warm-card border border-warm-border hover:border-brand-teal/35 rounded-2xl p-5 transition-all hover:-translate-y-0.5 shadow-card hover:shadow-glow-teal group"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className={clsx(
          'text-xs font-medium px-2.5 py-1 rounded-full border capitalize',
          CATEGORY_COLORS[nudge.category] ?? 'bg-warm-inset text-ink-muted border-warm-border'
        )}>
          {nudge.category}
        </span>
        <span className="text-xs text-ink-soft shrink-0">
          {Math.round(nudge.effectiveness_score * 100)}% effective
        </span>
      </div>
      <h3 className="font-semibold text-ink text-sm mb-1 group-hover:text-brand-teal transition-colors">
        {nudge.title}
      </h3>
      <p className="text-xs text-ink-muted line-clamp-2 leading-relaxed">{nudge.body}</p>
    </button>
  )
}

function NudgePreviewModal({ nudge, onClose }: { nudge: Nudge; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 bg-ink/20 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-warm-card border border-warm-border rounded-2xl p-6 max-w-lg w-full shadow-card">
        <div className="flex items-start justify-between mb-4">
          <span className={clsx(
            'text-xs font-medium px-2.5 py-1 rounded-full border capitalize',
            CATEGORY_COLORS[nudge.category] ?? 'bg-warm-inset text-ink-muted border-warm-border'
          )}>
            {nudge.category}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-soft hover:text-ink text-xl leading-none"
          >
            &times;
          </button>
        </div>

        <h2 className="text-lg font-bold text-ink mb-3">{nudge.title}</h2>

        <div className="bg-warm-inset rounded-xl p-4 mb-4" style={{border: '1px solid #e0d9cc', borderLeft: '4px solid #2A7A8A'}}>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-5 h-5 bg-brand-teal rounded flex items-center justify-center">
              <Zap size={11} className="text-white" />
            </div>
            <span className="text-xs font-semibold text-ink">Duva</span>
            <span className="text-xs text-ink-soft">nudge bot</span>
          </div>
          <p className="text-sm text-ink-muted leading-relaxed">{nudge.body}</p>
        </div>

        <div className="space-y-2 text-xs text-ink-soft">
          {nudge.science_source && (
            <p>📚 <span className="text-ink-muted">{nudge.science_source}</span></p>
          )}
          <p>Channel: <span className="text-ink-muted capitalize">{nudge.channel}</span></p>
          <p>
            Type:{' '}
            <span className="text-ink-muted capitalize">
              {nudge.system_target === 'system1' ? 'System 1 (fast, intuitive)' : 'System 2 (deliberate, reflective)'}
            </span>
          </p>
          <p>
            Effectiveness:{' '}
            <span className="text-brand-teal font-semibold">{Math.round(nudge.effectiveness_score * 100)}%</span>
          </p>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="mt-5 w-full text-white font-medium text-sm py-2.5 rounded-full transition-colors shadow-glow-teal"
          style={{ background: '#2A7A8A' }}
          onMouseEnter={e => (e.currentTarget.style.background = '#236b7a')}
          onMouseLeave={e => (e.currentTarget.style.background = '#2A7A8A')}
        >
          Close
        </button>
      </div>
    </div>
  )
}
